import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Pane,
  TextInput,
  Button,
  Spinner,
  toaster,
  Heading,
  Card,
  IconButton,
  Dialog,
  Badge,
  EditIcon,
  TrashIcon,
  Text
} from 'evergreen-ui';
import MDEditor from '@uiw/react-md-editor';
import api from '../../services/api';
import { useProject } from '../../context/ProjectContext';

const NoteEditor = ({ hostId, domainId, portId, compact = false }) => {
  const { currentProject } = useProject();
  const [notes, setNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const editorRefCompact = useRef(null);
  const editorRefFull = useRef(null);
  const [newNote, setNewNote] = useState({
    title: '',
    content: '',
    host_id: hostId,
    domain_id: domainId,
    port_id: portId
  });

  // Find textarea inside MDEditor
  const findTextarea = (ref) => {
    if (!ref || !ref.current) return null;
    const textarea = ref.current.querySelector('textarea');
    return textarea;
  };

  const handleImagePaste = async (event, isSelectedNote) => {
    const items = event.clipboardData?.items;
    if (!items || !currentProject?.id) return;

    const imageFiles = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type && item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }

    if (imageFiles.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      setUploadingImage(true);
      
      // Get current content and cursor position
      const currentContent = isSelectedNote ? (selectedNote?.content || '') : (newNote.content || '');
      const textarea = event.target;
      let cursorStart = currentContent.length;
      let cursorEnd = currentContent.length;
      
      if (textarea && (textarea.tagName === 'TEXTAREA' || textarea.tagName === 'INPUT')) {
        cursorStart = textarea.selectionStart || currentContent.length;
        cursorEnd = textarea.selectionEnd || currentContent.length;
      }
      
      try {
        for (const file of imageFiles) {
          if (file.size > 10 * 1024 * 1024) {
            toaster.danger(`Image ${file.name || 'image'} is too large (max 10MB)`);
            continue;
          }

          const formData = new FormData();
          formData.append('file', file);
          
          const response = await api.post(
            `/projects/${currentProject.id}/markdown-images`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );

          const imageUrl = response.data.url;
          const fileName = file.name || 'image';
          const imageMarkdown = `![${fileName}](${imageUrl})\n`;
          
          const before = currentContent.substring(0, cursorStart);
          const after = currentContent.substring(cursorEnd);
          const newContent = before + imageMarkdown + after;
          
          if (isSelectedNote) {
            setSelectedNote(prev => {
              const updated = { ...prev, content: newContent };
              setTimeout(() => {
                const updatedTextarea = findTextarea(editorRefCompact) || findTextarea(editorRefFull);
                if (updatedTextarea) {
                  const newPos = cursorStart + imageMarkdown.length;
                  updatedTextarea.setSelectionRange(newPos, newPos);
                  updatedTextarea.focus();
                }
              }, 0);
              return updated;
            });
          } else {
            setNewNote(prev => {
              const updated = { ...prev, content: newContent };
              setTimeout(() => {
                const updatedTextarea = findTextarea(editorRefCompact) || findTextarea(editorRefFull);
                if (updatedTextarea) {
                  const newPos = cursorStart + imageMarkdown.length;
                  updatedTextarea.setSelectionRange(newPos, newPos);
                  updatedTextarea.focus();
                }
              }, 0);
              return updated;
            });
          }
          
          cursorStart += imageMarkdown.length;
          cursorEnd = cursorStart;
          
          toaster.success('Image uploaded and inserted');
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        toaster.danger('Failed to upload image');
      } finally {
        setUploadingImage(false);
      }
    }
  };

  useEffect(() => {
    if (currentProject?.id) {
      fetchNotes();
    }
  }, [currentProject?.id, hostId, domainId, portId]);

  const fetchNotes = async () => {
    if (!currentProject?.id) return;
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (hostId) params.append('host_id', hostId);
      if (domainId) params.append('domain_id', domainId);
      if (portId) params.append('port_id', portId);

      const response = await api.get(
        `/projects/${currentProject.id}/notes?${params.toString()}`
      );
      setNotes(response.data);
    } catch (error) {
      toaster.danger('Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async () => {
    if (!currentProject?.id) return;
    
    try {
      const response = await api.post(
        `/projects/${currentProject.id}/notes`,
        newNote
      );
      setNotes([...notes, response.data]);
      setNewNote({ title: '', content: '', host_id: hostId, domain_id: domainId, port_id: portId });
      toaster.success('Note created successfully');
    } catch (error) {
      toaster.danger('Failed to create note');
    }
  };

  const handleUpdateNote = async () => {
    if (!selectedNote || !currentProject?.id) return;
    try {
      const response = await api.put(
        `/projects/${currentProject.id}/notes/${selectedNote.id}`,
        selectedNote
      );
      setNotes(notes.map(note => 
        note.id === selectedNote.id ? response.data : note
      ));
      setIsEditing(false);
      toaster.success('Note updated successfully');
    } catch (error) {
      toaster.danger('Failed to update note');
    }
  };

  const handleDeleteNote = async () => {
    if (!selectedNote || !currentProject?.id) return;
    try {
      await api.delete(`/projects/${currentProject.id}/notes/${selectedNote.id}`);
      setNotes(notes.filter(note => note.id !== selectedNote.id));
      setSelectedNote(null);
      setShowDeleteDialog(false);
      toaster.success('Note deleted successfully');
    } catch (error) {
      toaster.danger('Failed to delete note');
    }
  };

  const renderNoteContext = (note) => {
    if (note.host) {
      return (
        <Badge color="blue" marginRight={8}>
          Host: {note.host.ip_address}
        </Badge>
      );
    }
    if (note.domain) {
      return (
        <Badge color="green" marginRight={8}>
          Domain: {note.domain.name}
        </Badge>
      );
    }
    if (note.port) {
      return (
        <Badge color="purple" marginRight={8}>
          Port: {note.port.number}/{note.port.protocol}
          {note.port.host && ` (${note.port.host.ip_address})`}
        </Badge>
      );
    }
    return <Badge color="neutral">Project Note</Badge>;
  };

  if (compact) {
    return (
      <Pane height="100%">
        {isEditing ? (
          <Pane height="100%" display="flex" flexDirection="column">
            <Pane padding={8} borderBottom="1px solid #E6E8F0" display="flex" alignItems="center">
              <TextInput
                flex={1}
                placeholder="Note Title"
                value={selectedNote?.title || newNote.title}
                onChange={e => 
                  selectedNote
                    ? setSelectedNote({ ...selectedNote, title: e.target.value })
                    : setNewNote({ ...newNote, title: e.target.value })
                }
              />
              <Button
                appearance="minimal"
                marginLeft={8}
                onClick={() => {
                  setIsEditing(false);
                  if (!selectedNote) {
                    setNewNote({ title: '', content: '', host_id: hostId, domain_id: domainId, port_id: portId });
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                marginLeft={8}
                onClick={selectedNote ? handleUpdateNote : handleCreateNote}
              >
                Save
              </Button>
            </Pane>
            <Pane flex={1} padding={8} ref={editorRefCompact}>
              <MDEditor
                value={selectedNote?.content || newNote.content}
                onChange={value => 
                  selectedNote
                    ? setSelectedNote({ ...selectedNote, content: value || '' })
                    : setNewNote({ ...newNote, content: value || '' })
                }
                height="100%"
                preview="edit"
                data-color-mode="light"
                style={{ backgroundColor: 'white' }}
                onPaste={(event) => handleImagePaste(event, !!selectedNote)}
              />
              {uploadingImage && (
                <Pane marginTop={8} display="flex" alignItems="center" gap={8}>
                  <Spinner size={16} />
                  <Text size={300} color="muted">Uploading image...</Text>
                </Pane>
              )}
            </Pane>
          </Pane>
        ) : (
          <Pane height="100%">
            <Pane marginBottom={16} display="flex" justifyContent="flex-end">
              <Button
                appearance="primary"
                onClick={() => {
                  setSelectedNote(null);
                  setIsEditing(true);
                }}
              >
                Create Note
              </Button>
            </Pane>
            {notes.map(note => (
              <Pane
                key={note.id}
                padding={12}
                marginBottom={8}
                background="tint1"
                borderRadius={4}
              >
                <Pane display="flex" alignItems="center" marginBottom={8}>
                  <Heading size={400}>{note.title}</Heading>
                  <Pane marginLeft="auto">
                    <IconButton
                      icon={EditIcon}
                      appearance="minimal"
                      marginRight={8}
                      onClick={() => {
                        setSelectedNote(note);
                        setIsEditing(true);
                      }}
                      title="Edit note"
                    />
                    <IconButton
                      icon={TrashIcon}
                      appearance="minimal"
                      intent="danger"
                      onClick={() => {
                        setSelectedNote(note);
                        setShowDeleteDialog(true);
                      }}
                      title="Delete note"
                    />
                  </Pane>
                </Pane>
                <Pane marginBottom={8}>
                  {renderNoteContext(note)}
                </Pane>
                <Pane backgroundColor="white" padding={12} borderRadius={4}>
                  <MDEditor.Markdown 
                    source={note.content} 
                    data-color-mode="light"
                    style={{ backgroundColor: 'white', color: 'black' }}
                  />
                </Pane>
              </Pane>
            ))}
          </Pane>
        )}
        <Dialog
          isShown={showDeleteDialog}
          title="Delete Note"
          intent="danger"
          onCloseComplete={() => setShowDeleteDialog(false)}
          confirmLabel="Delete"
          onConfirm={handleDeleteNote}
        >
          Are you sure you want to delete this note? This action cannot be undone.
        </Dialog>
      </Pane>
    );
  }

  // Original full layout for the Notes section
  return (
    <Pane display="flex" height="100%">
      {/* Notes List Sidebar */}
      <Pane
        width={300}
        height="100%"
        borderRight
        background="tint1"
        padding={16}
        overflow="auto"
      >
        <Heading size={600} marginBottom={16}>Notes</Heading>
        <Button
          appearance="primary"
          iconBefore="plus"
          width="100%"
          marginBottom={16}
          onClick={() => {
            setSelectedNote(null);
            setIsEditing(true);
          }}
        >
          New Note
        </Button>

        {loading ? (
          <Pane display="flex" justifyContent="center" padding={16}>
            <Spinner />
          </Pane>
        ) : (
          notes.map(note => (
            <Card
              key={note.id}
              backgroundColor={selectedNote?.id === note.id ? 'tint2' : 'white'}
              padding={12}
              marginBottom={8}
              cursor="pointer"
              onClick={() => {
                setSelectedNote(note);
                setIsEditing(false);
              }}
              elevation={selectedNote?.id === note.id ? 2 : 0}
              hoverElevation={1}
            >
              <Heading size={400}>{note.title}</Heading>
              <Pane marginTop={8}>
                {renderNoteContext(note)}
                <Badge color="neutral" marginLeft={8}>
                  {new Date(note.updated_at).toLocaleDateString()}
                </Badge>
              </Pane>
            </Card>
          ))
        )}
      </Pane>

      {/* Note Editor/Viewer */}
      <Pane flex={1} padding={16} background="white" overflow="auto">
        {isEditing ? (
          <Pane>
            <TextInput
              width="100%"
              placeholder="Note Title"
              value={selectedNote?.title || newNote.title}
              onChange={e => 
                selectedNote
                  ? setSelectedNote({ ...selectedNote, title: e.target.value })
                  : setNewNote({ ...newNote, title: e.target.value })
              }
              marginBottom={16}
            />
            <Pane ref={editorRefFull}>
            <MDEditor
              value={selectedNote?.content || newNote.content}
              onChange={value => 
                selectedNote
                  ? setSelectedNote({ ...selectedNote, content: value || '' })
                  : setNewNote({ ...newNote, content: value || '' })
              }
              height={500}
              preview="edit"
              data-color-mode="light"
              style={{ backgroundColor: 'white' }}
                onPaste={(event) => handleImagePaste(event, !!selectedNote)}
              />
            </Pane>
            {uploadingImage && (
              <Pane marginTop={8} display="flex" alignItems="center" gap={8}>
                <Spinner size={16} />
                <Text size={300} color="muted">Uploading image...</Text>
              </Pane>
            )}
            <Pane marginTop={16} display="flex" justifyContent="flex-end">
              <Button
                marginRight={8}
                onClick={() => {
                  setIsEditing(false);
                  if (!selectedNote) {
                    setNewNote({ title: '', content: '', host_id: hostId, domain_id: domainId, port_id: portId });
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={selectedNote ? handleUpdateNote : handleCreateNote}
              >
                {selectedNote ? 'Update' : 'Create'} Note
              </Button>
            </Pane>
          </Pane>
        ) : selectedNote ? (
          <Pane>
            <Pane display="flex" alignItems="center" marginBottom={16}>
              <Heading size={700}>{selectedNote.title}</Heading>
              <Pane marginLeft="auto">
                <IconButton
                  icon={EditIcon}
                  appearance="minimal"
                  marginRight={8}
                  onClick={() => setIsEditing(true)}
                  title="Edit note"
                />
                <IconButton
                  icon={TrashIcon}
                  appearance="minimal"
                  intent="danger"
                  onClick={() => setShowDeleteDialog(true)}
                  title="Delete note"
                />
              </Pane>
            </Pane>
            <Pane backgroundColor="white" padding={12} borderRadius={4}>
              <MDEditor.Markdown 
                source={selectedNote.content} 
                data-color-mode="light"
                style={{ backgroundColor: 'white', color: 'black' }}
              />
            </Pane>
          </Pane>
        ) : (
          <Pane
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Heading size={500} color="muted">
              Select a note or create a new one
            </Heading>
          </Pane>
        )}
      </Pane>

      {/* Delete Confirmation Dialog */}
      <Dialog
        isShown={showDeleteDialog}
        title="Delete Note"
        intent="danger"
        onCloseComplete={() => setShowDeleteDialog(false)}
        confirmLabel="Delete"
        onConfirm={handleDeleteNote}
      >
        Are you sure you want to delete this note? This action cannot be undone.
      </Dialog>
    </Pane>
  );
};

export default NoteEditor; 